// https://www.figma.com/file/CcMNRwvjCIAU5YBULg4D4b/%F0%9F%A7%A9-Foundation-Library?node-id=1%3A8

import styled from '@emotion/styled';
import React from 'react';

import { pxToRem } from '../helpers/pxToRem';
import { fontNames } from '../styles/fonts';
import { DsColors, dsColors } from './variables';

const Heading = styled.h1({
    fontFamily: fontNames.dmSansBold,
    fontWeight: 700,
});
const Heading10 = styled(Heading.withComponent('h6'))({
    fontSize: pxToRem(14),
    lineHeight: pxToRem(20),
});
const Heading20 = styled(Heading.withComponent('h5'))({
    fontSize: pxToRem(16),
    lineHeight: pxToRem(20),
});
const Heading30 = styled(Heading.withComponent('h4'))({
    fontSize: pxToRem(18),
    lineHeight: pxToRem(24),
});
const Heading40 = styled(Heading.withComponent('h3'))({
    fontSize: pxToRem(22),
    lineHeight: pxToRem(28),
});
const Heading50 = styled(Heading.withComponent('h2'))({
    fontSize: pxToRem(28),
    lineHeight: pxToRem(36),
});
const Heading60 = styled(Heading.withComponent('h1'))({
    fontSize: pxToRem(32),
    lineHeight: pxToRem(40),
});

const Body = styled.span({
    fontFamily:
        '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif', // default font for all major OSs
    display: 'block',
    fontWeight: 400,
});
const BodyXSmall = styled(Body)({
    fontSize: pxToRem(12),
    lineHeight: pxToRem(16),
});
const BodySmall = styled(Body)({
    fontSize: pxToRem(14),
    lineHeight: pxToRem(20),
});
const BodyMedium = styled(Body)({
    fontSize: pxToRem(16),
    lineHeight: pxToRem(24),
});
const BodyLarge = styled(Body)({
    fontSize: pxToRem(18),
    lineHeight: pxToRem(28),
});
const BodyXLarge = styled(Body)({
    fontSize: pxToRem(22),
    lineHeight: pxToRem(32),
});

const Component = styled.span({
    fontFamily:
        '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif', // default font for all major OSs
    display: 'block',
    fontWeight: 500,
});
const ComponentXSmall = styled(Component)({
    fontSize: pxToRem(12),
    lineHeight: pxToRem(12),
});
const ComponentSmall = styled(Component)({
    fontSize: pxToRem(14),
    lineHeight: pxToRem(16),
});
const ComponentMedium = styled(Component)({
    fontSize: pxToRem(16),
    lineHeight: pxToRem(16),
});
const ComponentLarge = styled(Component)({
    fontSize: pxToRem(18),
    lineHeight: pxToRem(20),
});
const ComponentXLarge = styled(Component)({
    fontSize: pxToRem(22),
    lineHeight: pxToRem(24),
});

interface TextProps {
    /**
     * FontColor: this can be either a key of `color` object property or a hex string
     */
    fontColor?: LiteralUnion<DsColors>;
    marginTop?: number;
    marginBottom?: number;
    underline?: boolean;
    className?: string;
    centered?: boolean;
}

type TextComponentType = typeof Heading | typeof Body | typeof Component;
const createTextComponent = (TextComponent: TextComponentType): React.FC<TextProps> => ({
    children,
    fontColor = dsColors.neutral200,
    marginTop = 0,
    marginBottom = 0,
    className,
    centered = false,
}) => {
    const fontColorToApply = fontColor[0] === '#' ? fontColor : dsColors[fontColor];

    return (
        <TextComponent
            css={{
                color: fontColorToApply,
                marginTop: `${pxToRem(marginTop)} !important`, // !important has to be here to not let semantic.css overwrite
                marginBottom: `${pxToRem(marginBottom)} !important`, // !important has to be here to not let semantic.css overwrite
                textAlign: centered ? 'center' : 'initial',
            }}
            className={className}
        >
            {children}
        </TextComponent>
    );
};

export const HeadingText10 = createTextComponent(Heading10);
export const HeadingText20 = createTextComponent(Heading20);
export const HeadingText30 = createTextComponent(Heading30);
export const HeadingText40 = createTextComponent(Heading40);
export const HeadingText50 = createTextComponent(Heading50);
export const HeadingText60 = createTextComponent(Heading60);

export const BodyTextXSmall = createTextComponent(BodyXSmall);
export const BodyTextSmall = createTextComponent(BodySmall);
export const BodyTextMedium = createTextComponent(BodyMedium);
export const BodyTextLarge = createTextComponent(BodyLarge);
export const BodyTextXLarge = createTextComponent(BodyXLarge);

export const ComponentTextXSmall = createTextComponent(ComponentXSmall);
export const ComponentTextSmall = createTextComponent(ComponentSmall);
export const ComponentTextMedium = createTextComponent(ComponentMedium);
export const ComponentTextLarge = createTextComponent(ComponentLarge);
export const ComponentTextXLarge = createTextComponent(ComponentXLarge);
